import React from "react";
import { useNavigate } from "react-router";
import { useAuth } from "components/shared/AuthProvider";
import { useTheme } from "components/shared/ThemeProvider";
import Seo from "components/shared/Seo";
import styled from "styled-components";
import iconImage from "../../assets/images/icon.png";

const FullWidthBanner = styled.div`
    width: 100%;
    min-height: 500px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 60px 80px;

    box-sizing: border-box;

    @media (max-width: 768px) {
        flex-direction: column;
        padding: 40px 20px;
        min-height: 600px;
        text-align: center;
    }
`;

const LeftContent = styled.div`
    flex: 1;
    max-width: 600px;
    color: white;

    @media (max-width: 768px) {
        margin-bottom: 40px;
    }
`;

const BannerTitle = styled.h1`
    font-size: 3.5rem;
    font-weight: bold;
    margin-bottom: 20px;
    line-height: 1.2;

    @media (max-width: 768px) {
        font-size: 2.5rem;
    }

    @media (max-width: 480px) {
        font-size: 2rem;
    }
`;

const BannerSubtitle = styled.p`
    font-size: 1.3rem;
    margin-bottom: 40px;
    opacity: 0.9;
    line-height: 1.5;

    @media (max-width: 768px) {
        font-size: 1.1rem;
        margin-bottom: 30px;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 20px;

    @media (max-width: 480px) {
        flex-direction: column;
        gap: 15px;
    }
`;

const PrimaryButton = styled.button`
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border: 2px solid white;
    padding: 15px 30px;
    font-size: 1.1rem;
    font-weight: 600;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);

    &:hover {
        background: white;
        color: #667eea;
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
    }

    @media (max-width: 480px) {
        padding: 12px 24px;
        font-size: 1rem;
    }
`;

const SecondaryButton = styled.button`
    background: transparent;
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.5);
    padding: 15px 30px;
    font-size: 1.1rem;
    font-weight: 600;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        border-color: white;
        background: rgba(255, 255, 255, 0.1);
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
    }

    @media (max-width: 480px) {
        padding: 12px 24px;
        font-size: 1rem;
    }
`;

const RightContent = styled.div`
    flex: 0 0 400px;
    display: flex;
    justify-content: center;
    align-items: center;

    @media (max-width: 768px) {
        flex: none;
    }
`;

const BannerImage = styled.img`
    max-width: 100%;
    height: auto;
    max-height: 350px;
    border-radius: 15px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    transition: transform 0.3s ease;

    &:hover {
        transform: scale(1.05);
    }

    @media (max-width: 768px) {
        max-height: 250px;
    }
`;

// Features Section Styled Components
const FeaturesSection = styled.div`
    padding: 3rem 0;
    background-color: ${(props) => (props.$isDarkMode ? "#1a1a2e" : "white")};
    position: relative;
    z-index: 10;
`;

const FeaturesContainer = styled.div`
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 1rem;

    @media (min-width: 640px) {
        padding: 0 1.5rem;
    }

    @media (min-width: 1024px) {
        padding: 0 2rem;
    }
`;

const FeaturesHeader = styled.div`
    text-align: center;

    @media (min-width: 1024px) {
        text-align: center;
    }
`;

const FeaturesLabel = styled.h2`
    font-size: 0.875rem;
    color: #667eea;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    margin-bottom: 0.5rem;
`;

const FeaturesTitle = styled.p`
    margin-top: 0.5rem;
    font-size: 1.875rem;
    line-height: 2rem;
    font-weight: 800;
    letter-spacing: -0.025em;
    color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#111827")};

    @media (min-width: 640px) {
        font-size: 2.25rem;
        line-height: 2.5rem;
    }
`;

const FeaturesDescription = styled.p`
    margin-top: 1rem;
    max-width: 32rem;
    font-size: 1.25rem;
    line-height: 1.75rem;
    color: ${(props) => (props.$isDarkMode ? "#b0b0b0" : "#6b7280")};

    @media (min-width: 1024px) {
        margin-left: auto;
        margin-right: auto;
    }
`;

const FeaturesGrid = styled.div`
    margin-top: 5rem;
    display: grid;
    grid-template-columns: 1fr;
    gap: 3rem;

    @media (min-width: 640px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: 1024px) {
        grid-template-columns: repeat(3, 1fr);
    }
`;

const FeatureCard = styled.div`
    transition: all 0.3s ease;
    padding-top: 1.5rem;

    &:hover {
        transform: translateY(-5px);
    }
`;

const FeatureCardInner = styled.div`
    background-color: ${(props) => (props.$isDarkMode ? "#2a2a4a" : "#f9fafb")};
    border-radius: 0.5rem;
    padding: 1.5rem 1.5rem 2rem 1.5rem;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const FeatureCardContent = styled.div`
    margin-top: -1.5rem;
`;

const FeatureIconContainer = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.75rem;
    background-color: #667eea;
    border-radius: 0.375rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
        0 4px 6px -2px rgba(0, 0, 0, 0.05);
`;

const FeatureIcon = styled.div`
    height: 1.5rem;
    width: 1.5rem;
    color: white;
    font-size: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const FeatureTitle = styled.h3`
    margin-top: 2rem;
    font-size: 1.125rem;
    line-height: 1.75rem;
    font-weight: 500;
    color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#111827")};
    letter-spacing: -0.025em;
`;

const FeatureDescription = styled.p`
    margin-top: 1.25rem;
    font-size: 1rem;
    line-height: 1.5rem;
    color: ${(props) => (props.$isDarkMode ? "#b0b0b0" : "#6b7280")};
`;

// Get Started Section Styled Components
const GetStartedSection = styled.div`
    padding: 80px 0;
    background: ${(props) =>
        props.$isDarkMode
            ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
            : "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)"};
    position: relative;

    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: ${(props) =>
            props.$isDarkMode
                ? "radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%)"
                : "radial-gradient(circle at 20% 80%, rgba(74, 144, 226, 0.1) 0%, transparent 50%)"};
        pointer-events: none;
    }
`;

const GetStartedContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    text-align: center;
`;

const GetStartedTitle = styled.h2`
    font-size: 3rem;
    font-weight: 800;
    color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#2c3e50")};
    margin-bottom: 20px;
    background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: 768px) {
        font-size: 2.5rem;
    }

    @media (max-width: 480px) {
        font-size: 2rem;
    }
`;

const GetStartedSubtitle = styled.p`
    font-size: 1.3rem;
    color: ${(props) => (props.$isDarkMode ? "#b0b0b0" : "#6c757d")};
    margin-bottom: 60px;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;

    @media (max-width: 768px) {
        font-size: 1.1rem;
        margin-bottom: 40px;
        padding: 0 20px;
    }
`;

const PlatformGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 40px;
    margin-top: 50px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 30px;
        margin-top: 30px;
    }
`;

const PlatformCard = styled.div`
    background: ${(props) => (props.$isDarkMode ? "#2a2a4a" : "white")};
    border-radius: 20px;
    padding: 40px 30px;
    box-shadow: ${(props) =>
        props.$isDarkMode
            ? "0 10px 30px rgba(0, 0, 0, 0.3)"
            : "0 10px 30px rgba(0, 0, 0, 0.1)"};
    transition: all 0.3s ease;
    border: 1px solid
        ${(props) =>
            props.$isDarkMode
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.05)"};
    position: relative;
    overflow: hidden;

    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: ${(props) =>
            props.gradient ||
            "linear-gradient(135deg, #4a90e2 0%, #357abd 100%)"};
    }

    &:hover {
        transform: translateY(-10px);
        box-shadow: ${(props) =>
            props.$isDarkMode
                ? "0 20px 40px rgba(0, 0, 0, 0.4)"
                : "0 20px 40px rgba(0, 0, 0, 0.15)"};
    }
`;

const PlatformIcon = styled.div`
    width: 80px;
    height: 80px;
    border-radius: 20px;
    background: ${(props) =>
        props.gradient || "linear-gradient(135deg, #4a90e2 0%, #357abd 100%)"};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2.5rem;
    margin: 0 auto 25px;
`;

const PlatformTitle = styled.h3`
    font-size: 1.5rem;
    font-weight: 600;
    color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#2c3e50")};
    margin-bottom: 15px;
`;

const PlatformDescription = styled.p`
    color: ${(props) => (props.$isDarkMode ? "#b0b0b0" : "#6c757d")};
    line-height: 1.6;
    margin-bottom: 25px;
`;

const PlatformButton = styled.button`
    background: ${(props) =>
        props.gradient || "linear-gradient(135deg, #4a90e2 0%, #357abd 100%)"};
    color: white;
    border: none;
    padding: 12px 30px;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    display: inline-block;
    width: 100%;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
        opacity: 0.9;
    }

    &:active {
        transform: translateY(0);
    }
`;

const Home = () => {
    const { isLoading } = useAuth();
    const { isDarkMode } = useTheme();
    const nav = useNavigate();

    // Show loading while checking authentication status
    if (isLoading) {
        return <div>Loading...</div>;
    }

    const handleGetStarted = () => {
        // Scroll to the get started section
        const getStartedSection = document.getElementById(
            "get-started-section"
        );
        if (getStartedSection) {
            getStartedSection.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    };

    const handleLearnMore = () => {
        // Add your navigation logic here
        nav("/features");
    };

    // If user is not authenticated, stay on this page
    return (
        <>
            <Seo title="Wishliz - Smart Wishlist App" />
            {/* Banner */}
            <FullWidthBanner>
                <LeftContent>
                    <BannerTitle>Welcome to Wishliz</BannerTitle>
                    <BannerSubtitle>
                        Create and manage your wishlists with ease. Share your
                        dreams and discover amazing products that matter to you.
                    </BannerSubtitle>
                    <ButtonContainer>
                        <PrimaryButton onClick={handleGetStarted}>
                            Get Started
                        </PrimaryButton>
                        <SecondaryButton onClick={handleLearnMore}>
                            Learn More
                        </SecondaryButton>
                    </ButtonContainer>
                </LeftContent>
                <RightContent>
                    <BannerImage src={iconImage} alt="WishLiz App" />
                </RightContent>
            </FullWidthBanner>

            {/* Features */}
            <FeaturesSection $isDarkMode={isDarkMode}>
                <FeaturesContainer>
                    <FeaturesHeader>
                        <FeaturesLabel>Features</FeaturesLabel>
                        <FeaturesTitle $isDarkMode={isDarkMode}>
                            A better way to shop online
                        </FeaturesTitle>
                        <FeaturesDescription $isDarkMode={isDarkMode}>
                            We help you save both time and money with our smart
                            features.
                        </FeaturesDescription>
                    </FeaturesHeader>

                    <FeaturesGrid>
                        {/* Feature 1 */}
                        <FeatureCard>
                            <FeatureCardInner $isDarkMode={isDarkMode}>
                                <FeatureCardContent>
                                    <div>
                                        <FeatureIconContainer>
                                            <FeatureIcon>🔔</FeatureIcon>
                                        </FeatureIconContainer>
                                    </div>
                                    <FeatureTitle $isDarkMode={isDarkMode}>
                                        Smart Sale Notifications
                                    </FeatureTitle>
                                    <FeatureDescription
                                        $isDarkMode={isDarkMode}
                                    >
                                        Get instant alerts when your favorite
                                        items go on sale. No more missing out on
                                        great deals.
                                    </FeatureDescription>
                                </FeatureCardContent>
                            </FeatureCardInner>
                        </FeatureCard>

                        {/* Feature 2 */}
                        <FeatureCard>
                            <FeatureCardInner $isDarkMode={isDarkMode}>
                                <FeatureCardContent>
                                    <div>
                                        <FeatureIconContainer>
                                            <FeatureIcon>⏰</FeatureIcon>
                                        </FeatureIconContainer>
                                    </div>
                                    <FeatureTitle $isDarkMode={isDarkMode}>
                                        Less Screen Time
                                    </FeatureTitle>
                                    <FeatureDescription
                                        $isDarkMode={isDarkMode}
                                    >
                                        Spend less time browsing and more time
                                        enjoying. We show you only what matters.
                                    </FeatureDescription>
                                </FeatureCardContent>
                            </FeatureCardInner>
                        </FeatureCard>

                        {/* Feature 3 */}
                        <FeatureCard>
                            <FeatureCardInner $isDarkMode={isDarkMode}>
                                <FeatureCardContent>
                                    <div>
                                        <FeatureIconContainer>
                                            <FeatureIcon>💰</FeatureIcon>
                                        </FeatureIconContainer>
                                    </div>
                                    <FeatureTitle $isDarkMode={isDarkMode}>
                                        Price Tracking
                                    </FeatureTitle>
                                    <FeatureDescription
                                        $isDarkMode={isDarkMode}
                                    >
                                        Track price history and get notified
                                        when prices drop to your desired level.
                                    </FeatureDescription>
                                </FeatureCardContent>
                            </FeatureCardInner>
                        </FeatureCard>
                    </FeaturesGrid>
                </FeaturesContainer>
            </FeaturesSection>

            {/* Get Started Section */}
            <GetStartedSection
                id="get-started-section"
                $isDarkMode={isDarkMode}
            >
                <GetStartedContainer>
                    <GetStartedTitle $isDarkMode={isDarkMode}>
                        Choose Your Platform
                    </GetStartedTitle>
                    <GetStartedSubtitle $isDarkMode={isDarkMode}>
                        Get started with Wishliz on your favorite platform.
                        Download our mobile apps or access the web portal to
                        begin creating your wishlists.
                    </GetStartedSubtitle>

                    <PlatformGrid>
                        {/* Android App */}
                        <PlatformCard
                            gradient="linear-gradient(135deg, #3DDC84 0%, #2E7D32 100%)"
                            $isDarkMode={isDarkMode}
                        >
                            <PlatformIcon gradient="linear-gradient(135deg, #3DDC84 0%, #2E7D32 100%)">
                                🤖
                            </PlatformIcon>
                            <PlatformTitle $isDarkMode={isDarkMode}>
                                Android App
                            </PlatformTitle>
                            <PlatformDescription $isDarkMode={isDarkMode}>
                                Download our native Android app for the best
                                mobile experience. Get push notifications,
                                offline access, and seamless integration with
                                your device.
                            </PlatformDescription>
                            <PlatformButton
                                gradient="linear-gradient(135deg, #3DDC84 0%, #2E7D32 100%)"
                                onClick={() => {
                                    // Add your Google Play Store link here
                                    window.open(
                                        "https://play.google.com/store",
                                        "_blank"
                                    );
                                }}
                            >
                                Download for Android
                            </PlatformButton>
                        </PlatformCard>

                        {/* iOS App */}
                        <PlatformCard
                            gradient="linear-gradient(135deg, #007AFF 0%, #0051D5 100%)"
                            $isDarkMode={isDarkMode}
                        >
                            <PlatformIcon gradient="linear-gradient(135deg, #007AFF 0%, #0051D5 100%)">
                                📱
                            </PlatformIcon>
                            <PlatformTitle $isDarkMode={isDarkMode}>
                                iOS App
                            </PlatformTitle>
                            <PlatformDescription $isDarkMode={isDarkMode}>
                                Get our iOS app from the App Store. Enjoy native
                                iOS features, Face ID/Touch ID security, and
                                beautiful design that follows Apple's
                                guidelines.
                            </PlatformDescription>
                            <PlatformButton
                                gradient="linear-gradient(135deg, #007AFF 0%, #0051D5 100%)"
                                onClick={() => {
                                    // Add your App Store link here
                                    window.open(
                                        "https://apps.apple.com/app-store",
                                        "_blank"
                                    );
                                }}
                            >
                                Download for iOS
                            </PlatformButton>
                        </PlatformCard>

                        {/* Web Portal */}
                        <PlatformCard
                            gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                            $isDarkMode={isDarkMode}
                        >
                            <PlatformIcon gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
                                🌐
                            </PlatformIcon>
                            <PlatformTitle $isDarkMode={isDarkMode}>
                                Web Portal
                            </PlatformTitle>
                            <PlatformDescription $isDarkMode={isDarkMode}>
                                Access Wishliz directly from your web browser.
                                No download required - just sign in and start
                                creating your wishlists from any device.
                            </PlatformDescription>
                            <PlatformButton
                                gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                                onClick={() => {
                                    // Navigate to login/register page
                                    nav("/auth/login");
                                }}
                            >
                                Access Web Portal
                            </PlatformButton>
                        </PlatformCard>
                    </PlatformGrid>
                </GetStartedContainer>
            </GetStartedSection>
        </>
    );
};

export default Home;
